<template>
  <div class="client-onboarding-page form-preview">
    <div class="pca-form">
      <div class="preview-banner mb-2">
        <feather-icon icon="EyeIcon" size="16" class="mr-50" />
        <div class="flex-grow-1">
          <strong>Preview</strong> — this is what the client sees. Nothing typed here is saved.
          <div v-if="payload.packages && payload.packages.length" class="small text-muted">
            Packages: <span v-for="(p, i) in payload.packages" :key="p.Name + i">{{ i ? ", " : "" }}{{ p.Name }}<em v-if="p.source"> ({{ p.source }})</em></span>
          </div>
        </div>
      </div>

      <div v-for="(step, index) in steps" :key="step.key" class="preview-step mb-2">
        <div class="section-head">
          <span class="section-letter" :class="{ 'section-letter-icon': step.icon, 'section-letter-sign': step.type === 'review' }">
            <feather-icon v-if="step.icon" :icon="step.icon" size="16" />
            <template v-else>{{ step.letter }}</template>
          </span>
          <div class="flex-grow-1">
            <h4 class="mb-0">{{ index + 1 }}. {{ step.title }}</h4>
            <small class="text-muted">{{ step.subtitle }}</small>
          </div>
          <b-badge v-if="step.signed" variant="light-secondary">
            <feather-icon icon="LockIcon" size="12" class="mr-25" />
            Signed
          </b-badge>
        </div>

        <div class="section-body">
          <validation-observer tag="div">
            <dynamic-section
              v-if="step.type === 'section'"
              :section="step.section"
              :form="form"
              :disabled="step.signed"
              :notice-signatures="payload.noticeSignatures || {}"
              preview
            />

            <person-list-section
              v-else-if="step.type === 'persons'"
              :config="step.config"
              :section="step.section"
              :value="form.persons[step.config.formKey] || []"
              :form="form"
              :requirements="payload.requirements || []"
              :disabled="step.signed"
              :can-upload="false"
              preview
              @input="setPersons(step.config.formKey, $event)"
            />

            <div v-else-if="step.type === 'documents'">
              <p class="text-muted small">Documents the client is asked to upload on this step:</p>
              <ul v-if="accountRequirements.length" class="mb-0">
                <li v-for="(row, i) in accountRequirements" :key="'req' + i">
                  {{ row.Name }}
                  <b-badge v-if="row.Required" variant="light-warning" class="ml-25">Required</b-badge>
                  <small v-if="row.PackageName" class="text-muted"> · {{ row.PackageName }}</small>
                  <small v-if="row.Note" class="text-muted d-block">{{ row.Note }}</small>
                </li>
              </ul>
              <p v-else class="text-muted mb-0">No account documents are requested.</p>
              <p v-if="personRequirements.length" class="text-muted small mt-1 mb-0">
                Per-person documents ({{ personRequirements.length }}) are shown inside each person's card above.
              </p>
            </div>

            <div v-else-if="step.type === 'review'">
              <dynamic-section
                v-if="reviewExtras"
                :section="reviewExtras"
                :form="form"
                :disabled="false"
                :notice-signatures="payload.noticeSignatures || {}"
                preview
                class="mb-2"
              />
              <section-declaration
                :section-e="form.sectionE"
                :section-f="form.sectionF || null"
                :declaration="declarationItem"
                :has-recat="hasRecat"
                :disabled="!!payload.signed && !payload.reopened"
                :signed-name="payload.signatureName"
                :signed-date="payload.signedDate"
              />
            </div>
          </validation-observer>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// The assembled form, every step stacked, rendered with the same components as the client
// wizard. Used by the admin preview (builder and onboarding detail). Edits stay local.
import { ValidationObserver } from "vee-validate";
import { normaliseForm } from "./formSchema";
import { personConfig } from "./personTypes";
import DynamicSection from "./DynamicSection.vue";
import PersonListSection from "./PersonListSection.vue";
import SectionDeclaration from "./sections/SectionDeclaration.vue";

export default {
  name: "FormPreview",

  components: { ValidationObserver, DynamicSection, PersonListSection, SectionDeclaration },

  props: {
    payload: { type: Object, required: true },
  },

  data() {
    return {
      form: normaliseForm(this.payload.formData, this.payload.formSchema),
    };
  },

  watch: {
    payload(next) {
      this.form = normaliseForm(next.formData, next.formSchema);
    },
  },

  computed: {
    sections() {
      return (this.payload.formSchema && this.payload.formSchema.sections) || [];
    },
    signedKeys() {
      return (this.payload.signed && this.payload.signedSections) || [];
    },
    sectionE() {
      return this.sections.find((section) => section.key === "E") || null;
    },
    declarationItem() {
      return this.sectionE ? this.sectionE.items.find((item) => item.key === "DECLARATION") || null : null;
    },
    reviewExtras() {
      if (!this.sectionE) return null;
      const items = this.sectionE.items.filter((item) => item.key !== "DECLARATION");
      return items.length ? { ...this.sectionE, items } : null;
    },
    hasRecat() {
      return this.sections.some((section) => section.items.some((item) => item.builtinKey === "recategorisation"));
    },
    steps() {
      const list = [];
      this.sections.forEach((section) => {
        if (section.key === "E") return;
        const signed = this.payload.reopened ? this.signedKeys.indexOf(section.key) !== -1 : !!this.payload.signed;
        if (section.personType) {
          const config = personConfig(section.personType, this.payload.personTypes);
          if (!config) return;
          list.push({ key: section.key, letter: null, icon: "UsersIcon", title: section.title || config.plural, subtitle: section.subtitle || config.subtitle, type: "persons", config, section, signed });
          return;
        }
        list.push({ key: section.key, letter: section.key.length <= 2 ? section.key : null, icon: section.key.length <= 2 ? null : "FileTextIcon", title: section.title, subtitle: section.subtitle, type: "section", section, signed });
      });
      list.push({ key: "DOCUMENTS", letter: null, icon: "FileTextIcon", title: "Documents", subtitle: "Documents requested by Swiss Finance Corporation", type: "documents" });
      list.push({ key: "E", letter: "E", icon: null, title: "Review & sign", subtitle: (this.sectionE && this.sectionE.subtitle) || "", type: "review" });
      return list;
    },
    accountRequirements() {
      return (this.payload.requirements || []).filter((row) => row.Scope === "Account");
    },
    personRequirements() {
      return (this.payload.requirements || []).filter((row) => row.Scope !== "Account");
    },
  },

  methods: {
    setPersons(formKey, list) {
      this.$set(this.form.persons, formKey, list);
    },
  },
};
</script>

<style lang="scss">
@import "./clientOnboarding.scss";

.form-preview {
  .preview-banner {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    background: #fff7e6;
    border: 1px solid #ffe0a3;
  }
  .preview-step {
    border: 1px solid #e6e8ee;
    border-radius: 0.5rem;
    background: #fff;
    overflow: hidden;
  }
  .section-body {
    padding: 1rem 1.25rem 1.25rem;
  }
}
</style>
